import { useEffect, useState } from 'react';
import { markPaywallSeen } from '@/lib/analytics/track';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { isIOSNative, isAndroidNative } from '@/lib/platform';
import { PaywallEmailCTA } from '@/components/paywall-email-cta';
import { PurchaseButton } from '@/components/purchase-button';
import type { AppStudentObjective } from '@/lib/student-objectives/objective-types';
import { formatTimer, formatPassRemaining } from '@/lib/exercise-v2/format-timer';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';
import {
  PassDurationGrid,
  passCheckoutLabel,
  passPitch,
  usePassChoice,
} from '@/components/pass-duration-grid';

type PassDuration = '24h' | '7d' | '30d';
type ChapterType = 'normal' | 'epreuves';

const PASS_PRICES: Record<ChapterType, Record<PassDuration, string>> = {
  normal: { '24h': '1,99 €', '7d': '3,99 €', '30d': '9,99 €' },
  epreuves: { '24h': '2,99 €', '7d': '5,99 €', '30d': '15,99 €' },
};

interface Props {
  objective: AppStudentObjective;
}

interface ActivePass {
  type: ChapterType;
  expires_at: string;
  /** Renseigné = passe récurrent (abonnement 7j/30j) ; null = accès 24 h unique. */
  stripe_subscription_id: string | null;
}

type AccessState =
  | { kind: 'subscription' } // abonnement mensuel (contenu normal) → pas de paywall
  | { kind: 'pass'; pass: ActivePass } // passe temporaire actif → carte + minuteur
  | { kind: 'none' }; // aucun accès → paywall

export function ObjectivePaywallBlock({ objective }: Props) {
  // Tunnel de conversion « contenu bloqué → achat » (0718).
  useEffect(() => markPaywallSeen('objectif'), []);
  const { user } = useAuth();
  const studentId = user?.id ?? '';

  const isWarmup = objective.template.type === 'warmup';
  const chapterType: ChapterType =
    objective.template.contentType === 'epreuves' ? 'epreuves' : 'normal';

  const { data: access, isLoading } = useQuery({
    queryKey: ['objective-pack-access', studentId, chapterType],
    enabled: !!studentId && !isWarmup,
    staleTime: 30_000,
    queryFn: async (): Promise<AccessState> => {
      // Subscription couvre uniquement le contenu 'normal'
      if (chapterType === 'normal') {
        const { data: hasSub } = await supabase.rpc('has_active_subscription', {
          p_user_id: studentId,
        });
        if (hasSub) return { kind: 'subscription' };
      }

      const typeFilter =
        chapterType === 'epreuves' ? 'type.eq.epreuves' : 'type.eq.normal,type.eq.epreuves';

      // Le passe le plus tardif (couvre le plus longtemps).
      const { data: pass } = await supabase
        .from('access_rights')
        .select('type, expires_at, stripe_subscription_id')
        .eq('user_id', studentId)
        .gt('expires_at', new Date().toISOString())
        .or(typeFilter)
        .order('expires_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return pass ? { kind: 'pass', pass: pass as ActivePass } : { kind: 'none' };
    },
  });

  if (isWarmup || isLoading || !access) return null;
  if (access.kind === 'subscription') return null;
  if (access.kind === 'pass') {
    return <ActivePassCard pass={access.pass} chapterType={chapterType} />;
  }
  return <PaywallCard chapterType={chapterType} />;
}

// ─── Passe actif (affiché en bas quand l'accès est déjà débloqué) ─────────────
// Minuteur vivant : un passe 24 h (accès unique, non récurrent) affiche un
// décompte à la seconde (H:MM:SS) ; un abonnement 7j/30j affiche un décompte
// grossier (jours/heures) rafraîchi à la minute.

function ActivePassCard({ pass, chapterType }: { pass: ActivePass; chapterType: ChapterType }) {
  const isOneTime = !pass.stripe_subscription_id;
  const expiresAtMs = new Date(pass.expires_at).getTime();
  const [remainingMs, setRemainingMs] = useState(() => expiresAtMs - Date.now());

  useEffect(() => {
    const compute = () => setRemainingMs(expiresAtMs - Date.now());
    compute();
    const id = setInterval(compute, isOneTime ? 1000 : 60_000);
    return () => clearInterval(id);
  }, [expiresAtMs, isOneTime]);

  // Une fois réellement expiré, on rebascule sur le paywall.
  if (remainingMs <= 0) return <PaywallCard chapterType={chapterType} />;

  const isEpreuves = pass.type === 'epreuves';
  const typeLabel = isEpreuves ? 'Passe épreuves' : 'Passe exercices';
  const expiresDate = new Date(pass.expires_at).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="border-xp-200 bg-xp-50 space-y-3 rounded-2xl border p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xp-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Passe actif
          </p>
          <h3 className="font-display text-ink-900 mt-0.5 text-base font-extrabold">{typeLabel}</h3>
        </div>
        <span className="bg-xp-100 text-xp-700 shrink-0 rounded-full px-2.5 py-0.5 font-sans text-xs font-semibold">
          Actif
        </span>
      </div>

      {isOneTime ? (
        <div className="border-xp-200 rounded-xl border bg-white px-4 py-3 text-center">
          <p className="text-ink-400 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Temps restant
          </p>
          <p className="font-display text-ink-900 mt-0.5 text-3xl font-extrabold tabular-nums">
            ⏱ {formatTimer(remainingMs)}
          </p>
        </div>
      ) : (
        <p className="text-ink-600 font-sans text-sm">
          <span className="font-semibold">{formatPassRemaining(remainingMs)}</span> restant ·
          renouvellement automatique
        </p>
      )}

      <p className="text-ink-500 font-sans text-xs">Expire le {expiresDate}</p>
    </div>
  );
}

function PaywallCard({ chapterType }: { chapterType: ChapterType }) {
  const {
    settings: offerSettings,
    duration: selectedDuration,
    setDuration: setSelectedDuration,
  } = usePassChoice('7d');
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [isParentLinkLoading, setIsParentLinkLoading] = useState(false);
  const [parentLinkUrl, setParentLinkUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(id);
  }, [copied]);

  async function handleCheckout() {
    setIsCheckoutLoading(true);
    markCheckoutStarted(passCheckoutMark(chapterType, selectedDuration));
    const { data, error } = await supabase.functions.invoke('create-pass-checkout', {
      body: { type: chapterType, duration: selectedDuration, return_to: window.location.href },
    });
    if (error || !data?.url) {
      setIsCheckoutLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  async function handleParentLink() {
    setIsParentLinkLoading(true);
    const { data, error } = await supabase.functions.invoke('create-parent-link', {
      body: { offer_type: chapterType, duration: selectedDuration },
    });
    setIsParentLinkLoading(false);
    if (error) return;
    setParentLinkUrl((data as { url: string }).url);
  }

  async function handleCopy() {
    if (!parentLinkUrl) return;
    await navigator.clipboard.writeText(parentLinkUrl);
    setCopied(true);
  }

  async function handleShare() {
    if (!parentLinkUrl || !navigator.share) return;
    await navigator.share({
      title: 'Paiement Jeraisonne',
      text: 'Clique ici pour payer mon accès Jeraisonne',
      url: parentLinkUrl,
    });
  }

  const isEpreuves = chapterType === 'epreuves';

  // ── iOS natif → IAP RevenueCat (App Store Guideline 3.1.1) ───────────────
  if (isIOSNative()) {
    return (
      <div className="border-primary-200 from-primary-50 bg-linear-to-b to-ink-50 space-y-4 rounded-2xl border-2 p-5">
        <div className="space-y-1 text-center">
          <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
            {isEpreuves ? 'Passe épreuves requis' : 'Passe exercices requis'}
          </p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">
            {isEpreuves ? 'Débloque les épreuves officielles' : 'Débloque tous les exercices'}
          </h3>
          <p className="text-ink-500 font-sans text-sm leading-relaxed">
            Accès complet à Jeraisonne — résiliable à tout moment depuis les Réglages iOS.
          </p>
        </div>

        <PurchaseButton
          productId="jeraisonne_student_monthly"
          context={{
            source: 'objective-paywall-block',
            chapter_type: chapterType,
          }}
        />

        <p className="text-ink-400 text-center font-sans text-xs">
          Ton professeur peut aussi t'assigner des exercices gratuitement
        </p>
      </div>
    );
  }

  // ── Android natif → Reader app (Google Play Reader Apps policy) ───────────
  if (isAndroidNative()) {
    return (
      <div className="border-primary-200 from-primary-50 bg-linear-to-b to-ink-50 space-y-4 rounded-2xl border-2 p-5">
        <div className="space-y-1 text-center">
          <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
            {isEpreuves ? 'Passe épreuves requis' : 'Passe exercices requis'}
          </p>
          <h3 className="font-display text-ink-900 text-xl font-extrabold">
            {isEpreuves ? 'Débloque les épreuves officielles' : 'Débloque tous les exercices'}
          </h3>
          <p className="text-ink-500 font-sans text-sm leading-relaxed">
            {isEpreuves
              ? "Continue avec l'accès aux épreuves officielles (Brevet, Bac…). On peut t'envoyer les formules par email."
              : "Continue avec un accès complet à Jeraisonne. On peut t'envoyer les formules par email."}
          </p>
        </div>

        <PaywallEmailCTA
          context={{
            source: 'objective-paywall-block',
            chapter_type: chapterType,
          }}
        />

        <p className="text-ink-400 text-center font-sans text-xs">
          Ton professeur peut aussi t'assigner des exercices gratuitement
        </p>
      </div>
    );
  }

  return (
    <div className="border-primary-200 from-primary-50 bg-linear-to-b to-ink-50 space-y-4 rounded-2xl border-2 p-5">
      <div className="space-y-1 text-center">
        <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
          {isEpreuves ? 'Passe épreuves requis' : 'Passe exercices requis'}
        </p>
        <h3 className="font-display text-ink-900 text-xl font-extrabold">
          {isEpreuves ? 'Débloque les épreuves officielles' : 'Débloque tous les exercices'}
        </h3>
        <p className="text-ink-500 font-sans text-sm leading-relaxed">
          {isEpreuves
            ? `Accès aux épreuves officielles (Brevet, Bac…). ${passPitch(offerSettings)}`
            : `La première étape de chaque exercice reste gratuite. ${passPitch(offerSettings)}`}
        </p>
      </div>

      <PassDurationGrid
        prices={PASS_PRICES[chapterType]}
        value={selectedDuration}
        onChange={setSelectedDuration}
        settings={offerSettings}
      />

      <button
        onClick={handleCheckout}
        disabled={isCheckoutLoading}
        className="bg-primary-400 font-display hover:bg-primary-500 min-h-11 w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
      >
        {isCheckoutLoading
          ? 'Redirection…'
          : passCheckoutLabel(
              selectedDuration,
              PASS_PRICES[chapterType][selectedDuration],
              offerSettings,
            )}
      </button>

      <div className="flex items-center gap-2">
        <div className="border-ink-200 flex-1 border-t" />
        <span className="text-ink-400 font-sans text-xs">ou</span>
        <div className="border-ink-200 flex-1 border-t" />
      </div>

      <button
        onClick={handleParentLink}
        disabled={isParentLinkLoading || !!parentLinkUrl}
        className="text-primary-600 border-primary-200 min-h-11 w-full rounded-xl border py-3 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
      >
        {isParentLinkLoading ? 'Génération…' : 'Demander à mes parents'}
      </button>

      {parentLinkUrl && (
        <div className="bg-ink-50 border-ink-200 space-y-3 rounded-xl border p-4">
          <p className="text-ink-700 font-sans text-xs font-semibold">
            Partage ce lien à tes parents — ils peuvent payer sans créer de compte :
          </p>
          <div className="border-ink-200 rounded-lg border bg-white px-3 py-2">
            <p className="text-ink-600 break-all font-mono text-[11px]">{parentLinkUrl}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="border-ink-200 flex-1 rounded-lg border py-2 font-sans text-xs font-semibold transition-all active:scale-[.98]"
            >
              {copied ? 'Copié !' : 'Copier le lien'}
            </button>
            {typeof navigator.share === 'function' && (
              <button
                onClick={handleShare}
                className="bg-primary-400 flex-1 rounded-lg py-2 font-sans text-xs font-semibold text-white transition-all active:scale-[.98]"
              >
                Partager
              </button>
            )}
          </div>
        </div>
      )}

      <p className="text-ink-400 text-center font-sans text-xs">
        Ton professeur peut aussi t'assigner des exercices gratuitement
      </p>
    </div>
  );
}
